import React from "react";

import { tracking } from "@iwoca/frontend-tracking-library";
import { useSearchParams } from "react-router";

import TokenExpired from "../TokenViews/TokenExpired";
import TokenFailure from "../TokenViews/TokenFailure";
import TokenUsed from "../TokenViews/TokenUsed";

const REQUEST_NEW_LINK_URL = "/login/passwordless/email";

type Reason =
  | "superseded"
  | "already_used"
  | "expired"
  | "too_old"
  | "future_issued"
  | "username_mismatch"
  | "not_found"
  | "signature_invalid"
  | "parse_error"
  | "missing_fields"
  | "internal_error"
  | "unknown";

const KNOWN_REASONS = new Set<Reason>([
  "superseded",
  "already_used",
  "expired",
  "too_old",
  "future_issued",
  "username_mismatch",
  "not_found",
  "signature_invalid",
  "parse_error",
  "missing_fields",
  "internal_error",
  "unknown",
]);

function parseReason(raw: string | null): Reason {
  if (raw && (KNOWN_REASONS as Set<string>).has(raw)) {
    return raw as Reason;
  }
  return "unknown";
}

// Rendered when auth-service's /login/magic-link/callback 302s here with
// ?reason=<slug> after the Cognito verify lambda throws MAGIC_LINK_<CODE>.
const MagicLinkError: React.FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const reason = parseReason(searchParams.get("reason"));

  React.useEffect(() => {
    if (reason === "internal_error" || reason === "unknown") {
      tracking.ampli.loginError();
    } else {
      tracking.ampli.loginFailed();
    }
  }, [reason]);

  switch (reason) {
    case "superseded":
    case "already_used":
      return <TokenUsed ctaURL={REQUEST_NEW_LINK_URL} />;

    case "expired":
    case "too_old":
    case "future_issued":
    case "not_found":
      return <TokenExpired ctaUrl={REQUEST_NEW_LINK_URL} />;

    case "signature_invalid":
    case "username_mismatch":
    case "parse_error":
    case "missing_fields":
    case "internal_error":
    case "unknown":
    default:
      return <TokenFailure />;
  }
};

export default MagicLinkError;
